import { PropsWithChildren, useEffect, useRef, useState } from 'react'
import { useAppDispatch, useAppSelector, RootState } from '../utils/agent.store'
import {
  msBackendAuthInit,
  initiateLogin,
  UserTypeEnum,
  MS_BACKEND_LOGIN_ATTEMPT_KEY,
} from '@app-store'
import { CustomButton, Loader } from '@ui-kit'
import { broadcastLoginSuccess } from '@ecrm-session'
import NavigationListener from '../components/common/NavigationListner'
import { MS_BACKEND_AUTH_STRINGS } from '../utils/strings'

// ─── Internal UI ──────────────────────────────────────────────────────────────

const Spinner: React.FC = () => (
  <Loader loaderText={MS_BACKEND_AUTH_STRINGS.AUTHENTICATING} />
)

/**
 * Reached when the saga finished (checking=false) with no error but
 * isAuthenticated is also false — e.g. stale/inconsistent persisted session
 * state (token present without isAuthenticated, or leftover data from
 * testing a different auth provider in the same tab). Rather than spin
 * forever with no way out, treat it as unauthenticated and re-attempt login.
 */
const RedirectToMsLogin: React.FC = () => {
  useEffect(() => {
    sessionStorage.setItem(MS_BACKEND_LOGIN_ATTEMPT_KEY, '1')
    // returnPath is a RELATIVE UI path (path + query + hash) — the backend
    // appends it to its allowlisted UI origin and rejects an absolute URL
    // (AUTH_REQUEST_INVALID), so not window.location.href.
    const returnPath =
      window.location.pathname + window.location.search + window.location.hash
    initiateLogin(returnPath)
  }, [])
  return <Spinner />
}

const AuthErrorPage: React.FC<{ message: string }> = ({ message }) => (
  <div className="flex items-center justify-center min-h-screen bg-accent-100">
    <div className="bg-accent-0 rounded-lg shadow-md p-6 max-w-md text-center">
      <h1 className="text-base font-medium text-error-500">
        {MS_BACKEND_AUTH_STRINGS.SIGN_IN_FAILED}
      </h1>
      <p className="mt-4 text-accent-600 font-light text-xs">{message}</p>
      <div className="pt-4">
        <CustomButton
          type="button"
          isFilled={true}
          horizontalPaddingClasses="px-4.5"
          verticalPaddingClasses="py-2"
          onClick={() => {
            sessionStorage.removeItem(MS_BACKEND_LOGIN_ATTEMPT_KEY)
            window.location.href = window.location.origin
          }}
          dataQaId={'try-again-button-ms-backend-auth-error'}
        >
          {MS_BACKEND_AUTH_STRINGS.TRY_AGAIN}
        </CustomButton>
      </div>
    </div>
  </div>
)

// ─── Guard ────────────────────────────────────────────────────────────────────

/**
 * Route guard for the backend-driven Microsoft OAuth flow (AUTH_PROVIDER.MS_BACKEND).
 *
 * Always starts with a full-screen spinner regardless of what redux-persist
 * rehydrated — this prevents a flash of app content before the token has been
 * re-validated by the backend.
 *
 * The saga signals completion by setting session.msBackendAuthChecking to false.
 * The guard watches for that true→false transition (never reacting to the
 * persisted initial value) before revealing the final UI.
 *
 * States:
 *   - spinner (pending)    → saga has not yet signalled completion
 *   - children             → saga succeeded (session.isAuthenticated === true)
 *   - error page           → saga set session.msBackendAuthError
 *   - redirect-to-login    → saga finished but no auth and no error (stale/
 *                            inconsistent persisted session state) — don't
 *                            hang forever, re-attempt login instead
 */
const MsBackendAuthGuard: React.FC<PropsWithChildren> = ({ children }) => {
  const dispatch = useAppDispatch()
  const isAuthenticated = useAppSelector(
    (s: RootState) => s.session.isAuthenticated
  )
  const authError = useAppSelector(
    (s: RootState) => s.session.msBackendAuthError ?? null
  )
  // Redux flag set true at saga start, false at saga end (success or error).
  const reduxChecking = useAppSelector(
    (s: RootState) => s.session.msBackendAuthChecking ?? true
  )

  // Local pending state — always starts true on mount, never relies on persist.
  const [pending, setPending] = useState(true)

  // dispatched ref prevents double-dispatch in StrictMode.
  const dispatched = useRef(false)
  // Latches true once this mount has rendered an authenticated session.
  // Logout (performAgentDeskLogout) clears session.isAuthenticated and then
  // owns navigating the browser away on its own — it does not unmount this
  // guard first. Without this latch, that isAuthenticated flip is
  // indistinguishable from "boot finished, no session" and falls through to
  // RedirectToMsLogin, which sets the login-attempt flag and starts its own
  // MS-login redirect — racing the logout's real navigation and leaving the
  // flag set for a trip that never actually happened.
  const wasAuthenticated = useRef(false)
  // Captures the redux checking value AFTER the dispatch effect runs,
  // so we can detect the true→false transition rather than reacting to the
  // persisted initial value.
  const prevReduxChecking = useRef<boolean | undefined>(undefined)
  // True when THIS tab has just come back from the Microsoft round trip, as
  // opposed to merely reloading an already-authenticated tab.
  const cameFromInteractiveLogin = useRef(false)
  const broadcastedLogin = useRef(false)

  // Effect 1: dispatch the saga once. Runs first in the commit.
  useEffect(() => {
    if (dispatched.current) return
    dispatched.current = true
    // Read BEFORE the saga runs: it clears this flag the moment the token
    // exchange succeeds, so this is the only chance to observe it.
    cameFromInteractiveLogin.current =
      sessionStorage.getItem(MS_BACKEND_LOGIN_ATTEMPT_KEY) === '1'
    prevReduxChecking.current = reduxChecking // capture persisted value
    dispatch(msBackendAuthInit({ userType: UserTypeEnum.AGENT }))
  }, [dispatch]) // eslint-disable-line react-hooks/exhaustive-deps

  // Effect 3: tell sibling tabs parked on /logout that a login just succeeded,
  // so each can resume the page IT was on (every tab stores its own return path
  // in sessionStorage). MSAL does this from main.tsx via resumeAfterMsalLogin();
  // MS_BACKEND never goes through that path, so before this the followers had no
  // signal at all and ended up on the dashboard.
  //
  // Gated on cameFromInteractiveLogin so a plain reload of one tab doesn't yank
  // unrelated tabs off the logout screen.
  useEffect(() => {
    if (!isAuthenticated) return
    if (!cameFromInteractiveLogin.current) return
    if (broadcastedLogin.current) return
    broadcastedLogin.current = true
    broadcastLoginSuccess()
  }, [isAuthenticated])

  // Effect 2: watch for the saga's true→false signal. Runs second.
  // Only clears pending when reduxChecking transitions from true to false
  // AFTER the saga started — ignores whatever value was in redux-persist.
  useEffect(() => {
    if (!dispatched.current) return
    if (reduxChecking === false && prevReduxChecking.current === true) {
      setPending(false)
    }
    prevReduxChecking.current = reduxChecking
  }, [reduxChecking])

  if (pending) return <Spinner />

  if (authError) return <AuthErrorPage message={authError} />

  if (isAuthenticated) {
    wasAuthenticated.current = true
    return (
      <>
        <NavigationListener />
        {children}
      </>
    )
  }

  if (wasAuthenticated.current) {
    // isAuthenticated just flipped false after a real session. Two very
    // different things land here:
    //
    //   1. A logout — it hard-navigates the browser itself, so waiting is right.
    //   2. An AUTHORIZATION failure (user not enabled / no clients): the auth
    //      saga calls clearSession() and then requests an IN-APP redirect to
    //      /error/user-not-found via `navigateTo`.
    //
    // Case 2 used to hang forever: rendering only a Spinner unmounted the
    // NavigationListener below along with `children`, so the pending redirect
    // was never consumed and the user sat on "Authenticating…" instead of ever
    // seeing Access Denied. Keep the listener mounted here so an in-app
    // redirect can complete, while still showing the spinner rather than
    // protected content.
    return (
      <>
        <NavigationListener />
        <Spinner />
      </>
    )
  }

  return <RedirectToMsLogin />
}

export default MsBackendAuthGuard
